import {
  ADMIN_FEE_RATE,
  CAREGIVER_HEAVY_RATE_CENTS,
  CAREGIVER_STANDARD_RATE_CENTS
} from './config';

const KEYWORDS = {
  caregiver: ['caregiver', 'home care', 'home support', 'aidant', 'aide', 'companion', 'companionship', 'support worker', 'soignant', 'soins a domicile', 'soins à domicile', 'prepose', 'préposé', 'auxiliaire de vie', 'home aide', 'palliative', 'dementia', 'alzheimer'],
  nurse: ['nurse', 'auxiliary nurse', 'infirm', 'blood draw', 'blood', 'injection', 'wound', 'dressing', 'ear wash', 'prelevement', 'analyse'],
  nutrition: ['nutrition', 'diet', 'nutritionist'],
  therapy: ['therapy', 'mental', 'psych', 'counselling', 'counseling', 'social worker', 'therap'],
  foot: ['foot', 'podiat', 'chiropody'],
  physio: ['physio', 'rehab', 'mobility']
};

const CAREGIVER_SERVICE_KEYWORDS = {
  companionship: ['companion', 'companionship', 'presence', 'visiting', 'social'],
  meal_prep: ['meal prep', 'prepare meals', 'meal assistance', 'food prep', 'cooking', 'kitchen support'],
  medication_reminders: ['medication', 'medicine', 'med reminder', 'pill', 'pharmacy'],
  hygiene_support: ['hygiene', 'bath', 'bathing', 'toilet', 'grooming', 'wash'],
  mobility_support: ['mobility', 'walking', 'walker', 'ambulation', 'transfer', 'move']
};

const REQUESTED_SERVICE_MAP = {
  'companionship': 'companionship',
  'meal prep': 'meal_prep',
  'medication reminders': 'medication_reminders',
  'hygiene support': 'hygiene_support',
  'mobility support': 'mobility_support'
};

const CAREGIVER_SPECIAL_CASE_KEYWORDS = {
  palliative: ['palliative', 'end of life', 'fin de vie', 'hospice'],
  dementia: ['dementia', 'alzheimer', 'memory care', 'cognitive decline'],
  postSurgery: ['post surgery', 'post-surgery', 'after surgery', 'surgery', 'recovery', 'walker']
};

const CAREGIVER_PRIORITY_KEYWORDS = [
  'auxiliaire familiale',
  'préposée à domicile',
  'preposee a domicile',
  'préposé à domicile',
  'prepose a domicile',
  'préposé aux bénéficiaires',
  'prepose aux beneficiaires',
  'soutien à domicile',
  'soutien a domicile',
  'soins personnels',
  'préparation des repas',
  'preparation des repas'
];

const PROVIDER_TYPE_KEYWORDS = {
  caregiver: [
    'caregiver', 'home care', 'home support', 'support worker', 'aidant', 'aide', 'compagnie',
    'companionship', 'hygiene', 'medication reminder', 'daily living', 'fin de vie',
    'palliative', 'alzheimer', 'dementia', 'soignant', 'soins a domicile', 'soins à domicile',
    'prepose', 'préposé', 'prepose aux beneficiaires', 'préposé aux bénéficiaires', 'auxiliaire de vie',
    'assistance a domicile', 'assistance à domicile', 'aide a domicile', 'aide à domicile'
  ],
  nurse: [
    'nurse', 'auxiliary nurse', 'infirm', 'blood draw', 'injection', 'wound', 'dressing',
    'ear wash', 'lab', 'prelevement', 'analyse'
  ],
  other: [
    'nutrition', 'nutritionist', 'diet', 'therap', 'psych', 'mental', 'social worker',
    'physio', 'rehab', 'foot care', 'podiat', 'chiropody'
  ]
};

export function triageBookingIntent({ freeText, selectedRoute }) {
  if (selectedRoute) {
    return { providerRoute: selectedRoute, confidence: 'guided' };
  }

  const text = String(freeText || '').toLowerCase();
  const caregiverHits = KEYWORDS.caregiver.filter((keyword) => text.includes(keyword)).length;
  const nurseHits = KEYWORDS.nurse.filter((keyword) => text.includes(keyword)).length;

  if (caregiverHits >= nurseHits && caregiverHits > 0) {
    return { providerRoute: 'caregiver', confidence: 'text' };
  }

  if (nurseHits > 0) {
    return { providerRoute: 'nurse', confidence: 'text' };
  }

  return { providerRoute: 'other', confidence: 'fallback' };
}

export function determineCaregiverTier(assessment) {
  const needsHeavyCare =
    assessment?.autonomy === 'non_autonomous' ||
    assessment?.mobility === 'bedbound' ||
    assessment?.liftingRequired ||
    assessment?.transfersRequired ||
    assessment?.repositioningRequired ||
    assessment?.heavyHygieneSupport;

  return needsHeavyCare ? 'heavy' : 'standard';
}

function countKeywordHits(text, keywords, weight = 1) {
  return keywords.reduce((sum, keyword) => sum + (text.includes(keyword) ? weight : 0), 0);
}

function buildCaregiverIntent(routeContext) {
  const intakeText = [
    routeContext.intake?.healthSafetyNotes,
    routeContext.intake?.emergencySupportDetails,
    routeContext.intake?.preferences,
    routeContext.intake?.notes
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  const requestedServices = (routeContext.intake?.requestedServices || [])
    .map((service) => REQUESTED_SERVICE_MAP[String(service || '').toLowerCase()] || null)
    .filter(Boolean);

  const requestedKeywordGroups = requestedServices.flatMap((serviceKey) => CAREGIVER_SERVICE_KEYWORDS[serviceKey] || []);

  return {
    intakeText,
    requestedServices,
    requestedKeywordGroups,
    requestedPalliative: countKeywordHits(intakeText, CAREGIVER_SPECIAL_CASE_KEYWORDS.palliative) > 0,
    requestedDementia: countKeywordHits(intakeText, CAREGIVER_SPECIAL_CASE_KEYWORDS.dementia) > 0,
    requestedPostSurgery: countKeywordHits(intakeText, CAREGIVER_SPECIAL_CASE_KEYWORDS.postSurgery) > 0
  };
}

function classifyProviderRoute(provider) {
  const providerText = [
    provider.speciality_label,
    ...(provider.specialities || []).map((speciality) => speciality.name || speciality),
    ...(provider.services || []).map((service) => service.name || service.description || '')
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  const caregiverScore = countKeywordHits(providerText, PROVIDER_TYPE_KEYWORDS.caregiver, 2);
  const nurseScore = countKeywordHits(providerText, PROVIDER_TYPE_KEYWORDS.nurse, 2);
  const otherScore = countKeywordHits(providerText, PROVIDER_TYPE_KEYWORDS.other, 2);

  const hasExplicitOtherTitle = /nutrition|nutritionist|nutritionniste|psych|psychoth|therap|travailleuse sociale|travailleur social|social worker|physio|physioth|podiat|foot care|chirop/.test(providerText);
  const hasExplicitNurseTitle = /nurse|auxiliary nurse|infirm/.test(providerText);
  const hasExplicitCaregiverTitle = /caregiver|home care|home support|aidant|aide|support worker|companionship|fin de vie|palliative|dementia|alzheimer|soignant|soins a domicile|soins à domicile|prepose|préposé|prepose aux beneficiaires|préposé aux bénéficiaires|auxiliaire de vie|assistance a domicile|assistance à domicile|aide a domicile|aide à domicile/.test(providerText);

  if (hasExplicitOtherTitle && !hasExplicitCaregiverTitle && !hasExplicitNurseTitle) return 'other';
  if (hasExplicitNurseTitle && nurseScore >= caregiverScore) return 'nurse';
  if (hasExplicitCaregiverTitle && caregiverScore >= otherScore) return 'caregiver';
  if (nurseScore > caregiverScore && nurseScore >= otherScore) return 'nurse';
  if (caregiverScore > 0 && caregiverScore > otherScore) return 'caregiver';
  return 'other';
}

function scoreServiceMatch(routeContext, haystack) {
  const text = haystack.toLowerCase();
  if (routeContext.providerRoute === 'caregiver') {
    const caregiverIntent = buildCaregiverIntent(routeContext);
    let score = countKeywordHits(text, ['caregiver', 'home care', 'home support', 'aidant', 'aide', 'support worker'], 2);
    score += countKeywordHits(text, CAREGIVER_PRIORITY_KEYWORDS, 8);

    if (caregiverIntent.requestedKeywordGroups.length) {
      score += countKeywordHits(text, caregiverIntent.requestedKeywordGroups, 4);
    } else {
      score += countKeywordHits(text, [
        ...CAREGIVER_SERVICE_KEYWORDS.companionship,
        ...CAREGIVER_SERVICE_KEYWORDS.meal_prep,
        ...CAREGIVER_SERVICE_KEYWORDS.medication_reminders,
        ...CAREGIVER_SERVICE_KEYWORDS.hygiene_support,
        ...CAREGIVER_SERVICE_KEYWORDS.mobility_support
      ], 2);
    }

    if (caregiverIntent.requestedPostSurgery) {
      score += countKeywordHits(text, ['post surgery', 'post-surgery', 'recovery', 'mobility', 'walker'], 3);
    }

    const palliativeHits = countKeywordHits(text, CAREGIVER_SPECIAL_CASE_KEYWORDS.palliative, 5);
    const dementiaHits = countKeywordHits(text, CAREGIVER_SPECIAL_CASE_KEYWORDS.dementia, 4);

    if (caregiverIntent.requestedPalliative || routeContext.caregiverTier === 'heavy') {
      score += palliativeHits;
    } else {
      score -= palliativeHits * 2;
    }

    if (caregiverIntent.requestedDementia || routeContext.caregiverTier === 'heavy') {
      score += dementiaHits;
    } else {
      score -= dementiaHits * 2;
    }

    return score;
  }

  if (routeContext.providerRoute === 'nurse') {
    const subtype = routeContext.intake?.nurseServiceType || '';
    const subtypeKeywords = {
      blood_draw: ['blood', 'draw', 'lab', 'sample', 'analyse', 'prise de sang', 'prélèvement sanguin', 'prelevement sanguin', 'analyses de sang', 'test sanguin'],
      injection: ['inject', 'injection', 'injection de médicaments', 'injection de medicaments'],
      wound_care: ['wound', 'dressing', 'plaie', 'pansement', 'soin de plaie', 'changement de pansement'],
      ear_wash: ['ear', 'oreille'],
      other_nursing_support: ['nurse', 'infirm', 'soins infirmiers', 'soutien infirmier']
    }[subtype] || ['nurse', 'infirm'];

    return subtypeKeywords.reduce((sum, keyword) => sum + (text.includes(keyword) ? 3 : 0), 0);
  }

  const category = routeContext.intake?.otherServiceCategory;
  const categoryKeywords = {
    nutrition: KEYWORDS.nutrition,
    therapy: KEYWORDS.therapy,
    foot_care: KEYWORDS.foot,
    physiotherapy: KEYWORDS.physio,
    other: []
  }[category] || [];

  return categoryKeywords.reduce((sum, keyword) => sum + (text.includes(keyword) ? 3 : 0), 0);
}

export function matchProviders(providers, routeContext) {
  const caregiverIntent = routeContext.providerRoute === 'caregiver'
    ? buildCaregiverIntent(routeContext)
    : null;

  const rankedProviders = providers
    .map((provider) => {
      const matchedServices = (provider.services || [])
        .map((service) => {
          const searchable = [service.name, service.description, provider.speciality_label]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          const score = scoreServiceMatch(routeContext, searchable);
          return { ...service, matchScore: score };
        })
        .filter((service) => service.matchScore > 0)
        .sort((left, right) => right.matchScore - left.matchScore);

      const specialityText = (provider.specialities || []).map((speciality) => speciality.name).join(' ').toLowerCase();
      const providerText = [
        provider.speciality_label,
        provider.license_number,
        specialityText
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      const providerScore =
        matchedServices.reduce((sum, service) => sum + service.matchScore, 0) +
        scoreServiceMatch(routeContext, providerText);

      const speaksPreferredLanguage = (provider.languages || []).some(({ language }) =>
        String(language || '').toLowerCase().includes(String(routeContext.intake?.preferredLanguage || '').toLowerCase())
      );

      const heavyEligible = routeContext.providerRoute !== 'caregiver'
        ? true
        : routeContext.caregiverTier !== 'heavy'
          ? true
          : /palliative|alz|dementia|care|aid|support|infirm|nurse/.test(providerText);

      const providerRouteFit = classifyProviderRoute(provider);
      const providerModes = (provider.modes || []).map((mode) => String(mode || '').toLowerCase());
      const supportsInPersonCare = providerModes.length === 0 || providerModes.some((mode) => /in_person|home|domicile|person/.test(mode));
      const caregiverModeOverride = routeContext.providerRoute === 'caregiver'
        && /auxiliaire familiale|préposée? à domicile|preposee? a domicile|préposé aux bénéficiaires|prepose aux beneficiaires|soutien à domicile|soutien a domicile|soins personnels|préparation des repas|preparation des repas/.test(providerText);
      const routeMismatchPenalty =
        providerRouteFit === routeContext.providerRoute
          ? 0
          : routeContext.providerRoute === 'caregiver'
            ? providerRouteFit === 'other'
              ? 18
              : 10
            : routeContext.providerRoute === 'nurse'
              ? providerRouteFit === 'other'
                ? 16
                : 8
              : providerRouteFit === 'caregiver'
                ? 8
                : 10;

      const specialtyPenalty = routeContext.providerRoute === 'caregiver' && routeContext.caregiverTier === 'standard'
        ? (
            (!caregiverIntent?.requestedPalliative && /palliative|fin de vie|end of life|hospice/.test(providerText) ? 14 : 0) +
            (!caregiverIntent?.requestedDementia && /dementia|alzheimer|memory care/.test(providerText) ? 10 : 0)
          )
        : 0;

      return {
        ...provider,
        matchedServices,
        speaksPreferredLanguage,
        heavyEligible,
        providerRouteFit,
        supportsInPersonCare,
        caregiverModeOverride,
        bookingScore: providerScore + (speaksPreferredLanguage ? 4 : 0) - specialtyPenalty - routeMismatchPenalty
      };
    })
    .filter((provider) => provider.email_verified && provider.is_active)
    .filter((provider) => routeContext.providerRoute !== 'caregiver' || provider.supportsInPersonCare !== false || provider.caregiverModeOverride === true)
    .filter((provider) => provider.matchedServices.length > 0)
    .filter((provider) => provider.heavyEligible)
    .sort((left, right) => right.bookingScore - left.bookingScore);

  if (routeContext.providerRoute === 'caregiver') {
    const caregiverProviders = rankedProviders.filter((provider) => provider.providerRouteFit === 'caregiver');
    return caregiverProviders.length > 0 ? caregiverProviders : rankedProviders;
  }

  if (routeContext.providerRoute === 'nurse') {
    const nurseProviders = rankedProviders.filter((provider) => provider.providerRouteFit === 'nurse');
    return nurseProviders.length > 0 ? nurseProviders : rankedProviders;
  }

  const otherProviders = rankedProviders.filter((provider) => provider.providerRouteFit === 'other');
  return otherProviders.length > 0 ? otherProviders : rankedProviders;
}

export function calculateBookingPricing({ routeContext, selectedService, slot }) {
  const durationMinutes = slot?.durationMinutes || selectedService?.duration_minutes || 60;
  const durationHours = durationMinutes / 60;

  if (routeContext.providerRoute === 'caregiver') {
    const hourlyRateCents = routeContext.caregiverTier === 'heavy'
      ? CAREGIVER_HEAVY_RATE_CENTS
      : CAREGIVER_STANDARD_RATE_CENTS;
    const basePriceCents = Math.round(hourlyRateCents * durationHours);

    return {
      pricing_model: 'caregiver_hourly',
      durationMinutes,
      durationHours,
      hourlyRateCents,
      basePriceCents,
      adminFeeCents: 0,
      totalCents: basePriceCents
    };
  }

  const basePriceCents = selectedService?.price_cents || 0;
  const adminFeeCents = Math.round(basePriceCents * ADMIN_FEE_RATE);

  return {
    pricing_model: 'service_plus_admin_fee',
    durationMinutes,
    durationHours,
    hourlyRateCents: null,
    basePriceCents,
    adminFeeCents,
    totalCents: basePriceCents + adminFeeCents
  };
}

export function buildBookingSummary({ locale, routeContext, provider, service, slot, pricing }) {
  const formatter = new Intl.NumberFormat(locale === 'fr' ? 'fr-CA' : 'en-CA', {
    style: 'currency',
    currency: 'CAD'
  });

  return {
    providerName: `${provider.first_name} ${provider.last_name}`.trim(),
    providerType:
      routeContext.providerRoute === 'caregiver'
        ? routeContext.caregiverTier === 'heavy'
          ? 'caregiver_heavy'
          : 'caregiver_standard'
        : routeContext.providerRoute,
    serviceType:
      routeContext.providerRoute === 'nurse'
        ? routeContext.intake?.nurseServiceType
        : routeContext.providerRoute === 'other'
          ? routeContext.intake?.otherServiceCategory
          : service?.name || 'caregiver_support',
    slotLabel: slot?.label || '',
    startTime: slot?.start,
    endTime: slot?.end,
    durationLabel: `${pricing.durationHours.toFixed(pricing.durationHours % 1 === 0 ? 0 : 1)} h`,
    pricing,
    formattedBasePrice: formatter.format((pricing.basePriceCents || 0) / 100),
    formattedAdminFee: formatter.format((pricing.adminFeeCents || 0) / 100),
    formattedTotal: formatter.format((pricing.totalCents || 0) / 100)
  };
}
